import React, { useMemo } from 'react';
import styled from 'styled-components';

import RatingsReview from '@rover/components/src/RatingsReview/RatingsReview';
import EXPERIMENTS from '@rover/constants/src/platform/experiments.constants';
import DogTraining from '@rover/icons/svg/dog-training.svg';
import Rebook from '@rover/icons/svg/rebook.svg';
import Trophy from '@rover/icons/svg/trophy.svg';
import { Flex, Text } from '@rover/kibble/core';
import useExperimentsShim from '@rover/shared/js/hooks/useExperimentsShim';
import { sentenceCase } from '@rover/utilities/string';

import TruncatedText from '../../../components/TruncatedText';

const ICON_SIZE = 16;
const ROW_GAP_PX = 8;
const COLUMN_GAP_PX = 16;
const PILL_FIRST_COLUMN_WIDTH_PX = 170;
const PILL_REFLOW_WIDTH_PX = 428;

const GridContainer = styled.div<{ singleColumn?: boolean }>`
  display: grid;
  gap: ${ROW_GAP_PX}px ${COLUMN_GAP_PX}px;
  grid-template-columns: ${(props) =>
    props.singleColumn ? '' : `${PILL_FIRST_COLUMN_WIDTH_PX}px 1fr`};
  grid-template-areas: 'ratings method' 'yoe certs';
  width: 100%;

  /* Technically we care about the card width, but on narrow layouts, the card width is the viewport width */
  @media (max-width: ${PILL_REFLOW_WIDTH_PX}px) {
    display: flex;
    flex-direction: column;
    max-width: unset;
  }
`;

interface TrainerPillsProps {
  ratingsAverage?: string | null;
  reviewsCount: number;
  yearsOfExperience: string;
  methodology?: string | null;
  methodologyCustom?: string | null;
  repeatClientCount: number;
  isAllServicesClientCountActive: boolean;
}
export default function TrainerPills({
  ratingsAverage,
  reviewsCount,
  yearsOfExperience,
  methodology,
  methodologyCustom,
  repeatClientCount,
  isAllServicesClientCountActive,
}: TrainerPillsProps): React.ReactElement {
  const { isVariant } = useExperimentsShim();

  const isInRepeatTrainingClientCountVariant = isVariant(
    EXPERIMENTS.REPEAT_TRAINING_CLIENT_COUNT_EXPERIMENT
  );

  const shouldShowRatingsReviews = reviewsCount > 0;

  const shouldShowRepeatClientCount = repeatClientCount > 0;
  const trainingLabel = isAllServicesClientCountActive ? '' : 'training ';
  const repeatClientCountText = `${repeatClientCount} repeat ${trainingLabel}client${
    repeatClientCount === 1 ? '' : 's'
  }`;

  const methodologyText = useMemo(() => {
    const isCustomMethodology = !!methodologyCustom;
    const caseFormatted = sentenceCase(methodology ?? methodologyCustom ?? '');
    const formattedText = isCustomMethodology ? `Other: ${caseFormatted}` : caseFormatted;
    return `${formattedText} trainer`;
  }, [methodology, methodologyCustom]);

  const singleColumn = !shouldShowRatingsReviews;

  return (
    <GridContainer singleColumn={singleColumn}>
      {shouldShowRatingsReviews && (
        <Flex sx={{ gridArea: 'ratings' }}>
          <RatingsReview
            ratingsAverage={ratingsAverage ?? ''}
            reviewsCount={reviewsCount}
            fontWeight="regular"
          />
        </Flex>
      )}
      {isInRepeatTrainingClientCountVariant || isAllServicesClientCountActive ? (
        shouldShowRepeatClientCount && (
          <Flex gap="1x" /* No grid-area so that it can occupy first column when no reviews */>
            <Rebook width={ICON_SIZE} height={ICON_SIZE} />
            <TruncatedText size="100">{repeatClientCountText}</TruncatedText>
          </Flex>
        )
      ) : (
        <Flex gap="1x" /* No grid-area so that it can occupy first column when no reviews */>
          <DogTraining width={ICON_SIZE} height={ICON_SIZE} />
          <TruncatedText size="100">{methodologyText}</TruncatedText>
        </Flex>
      )}

      <Flex gap="1x" sx={{ gridArea: 'yoe' }}>
        <Trophy width={ICON_SIZE} height={ICON_SIZE} />
        <Text size="100">{yearsOfExperience}</Text>
      </Flex>
    </GridContainer>
  );
}
